import { ChangeDetectionStrategy, Component, input } from '@angular/core';

export type DashboardGap = 'sm' | 'md' | 'lg';

/**
 * DashboardLayout Component
 *
 * A responsive CSS Grid-based container for arranging dashboard widgets.
 * Automatically adjusts columns based on viewport width.
 *
 * @example
 * ```html
 * <zag-dashboard-layout [columns]="4" gap="lg">
 *   <zag-widget-card ... />
 *   <zag-widget-card ... />
 * </zag-dashboard-layout>
 * ```
 *
 * @see StatsWidget - For displaying KPI stat cards, use StatsWidget directly
 */
@Component({
  selector: 'zag-dashboard-layout',
  template: `<ng-content></ng-content>`,
  styleUrl: './dashboard-layout.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.zag-dashboard-layout]': 'true',
    '[class.zag-dashboard-layout--gap-sm]': 'gap() === "sm"',
    '[class.zag-dashboard-layout--gap-md]': 'gap() === "md"',
    '[class.zag-dashboard-layout--gap-lg]': 'gap() === "lg"',
    '[style.--dashboard-columns]': 'columns()',
    '[style.max-width]': 'maxWidth()',
  },
})
export class DashboardLayout {
  /** Number of columns on desktop (auto = responsive auto-fit) */
  readonly columns = input<number | 'auto'>(4);

  /** Gap between widgets */
  readonly gap = input<DashboardGap>('lg');

  /** Maximum content width */
  readonly maxWidth = input<string>('1400px');
}
